/**
 * Modal para responder un DTE recibido ante el SII (Ley 19.983).
 *
 * 🔴 Nace de un reporte de la contadora: aprobaba facturas de compra y el estado
 * en el SII no cambiaba. La causa era que `/purchases/invoices` mostraba un
 * modal que decia «¿Enviar el acuse de recibo al SII?» y mandaba **ACD**
 * (aceptar contenido) — que NO despeja la pestaña «Pendientes» del RCV. El
 * acuse es el **ERM**, y desde esa pantalla era inalcanzable.
 *
 * Medido en produccion cuando se diagnostico: 10 documentos con ACD y sin ERM,
 * todos pendientes en el RCV sin que nadie lo supiera.
 *
 * Por eso este modal **hace explicita la eleccion** en vez de esconderla detras
 * de un boton, y dice en lenguaje del receptor que hace cada opcion. Las siglas
 * van entre parentesis: quien aprueba facturas no tiene por que saber que ERM
 * significa «Otorga Recibo de Mercaderias».
 */
import { useEffect, useState } from 'react'
import { AlertTriangle, Ban } from 'lucide-react'

import { Modal } from '@/components/ds/Modal'

export type AccionSii = 'ERM' | 'ACD' | 'RCD' | 'RFP' | 'RFT'

/** Lo que devuelve el modal al confirmar. */
export interface RespuestaSii {
  accion: AccionSii
  recinto?: string
  observaciones?: string
}

const ACEPTACIONES: Array<{ valor: AccionSii; titulo: string; detalle: string }> = [
  {
    valor: 'ERM',
    titulo: 'Acusar recibo de la mercadería o servicio',
    // Lo que el receptor necesita saber para elegir: que esta es la que cambia
    // el estado en el SII. Sin esto, «aceptar» suena a lo mismo.
    detalle: 'Deja el documento fuera de «Pendientes» en el SII y habilita el pago a plazo. '
      + 'Es la que corresponde cuando ya se recibió lo facturado.',
  },
  {
    valor: 'ACD',
    titulo: 'Solo aceptar el contenido',
    detalle: 'Confirma que el documento está correcto, pero NO lo saca de «Pendientes» en el SII. '
      + 'Sirve cuando todavía no se recibe la mercadería.',
  },
]

const RECLAMOS: Array<{ valor: AccionSii; titulo: string; detalle: string }> = [
  { valor: 'RCD', titulo: 'El contenido está mal', detalle: 'Precio, cantidad o datos equivocados.' },
  { valor: 'RFP', titulo: 'Faltó parte de la mercadería', detalle: 'Llegó menos de lo facturado.' },
  { valor: 'RFT', titulo: 'No llegó nada', detalle: 'No se recibió la mercadería ni el servicio.' },
]

interface Props {
  open: boolean
  onClose: () => void
  onConfirm: (r: RespuestaSii) => void
  isPending?: boolean
  /** 'aceptar' ofrece ERM/ACD; 'reclamar' ofrece RCD/RFP/RFT. */
  modo: 'aceptar' | 'reclamar'
  /** Cómo identificar el documento en el encabezado. */
  documento: string
  /** Recinto configurado en el perfil de empresa; precarga el campo. */
  recintoPorDefecto?: string
  /** Factura conocida solo por el RCV (#1637): se responde sin el XML. */
  sinXml?: boolean
}

export function AcuseSiiModal({
  open, onClose, onConfirm, isPending, modo, documento, recintoPorDefecto = '', sinXml = false,
}: Props) {
  const esReclamo = modo === 'reclamar'
  const [accion, setAccion] = useState<AccionSii>(esReclamo ? 'RCD' : 'ERM')
  const [recinto, setRecinto] = useState(recintoPorDefecto)
  const [observaciones, setObservaciones] = useState('')

  // Al reabrir, volver al estado inicial: si no, el modal recuerda el motivo del
  // reclamo anterior y se manda al SII el de otro documento.
  useEffect(() => {
    if (open) {
      setAccion(esReclamo ? 'RCD' : 'ERM')
      setRecinto(recintoPorDefecto)
      setObservaciones('')
    }
  }, [open, esReclamo, recintoPorDefecto])

  const opciones = esReclamo ? RECLAMOS : ACEPTACIONES
  // Sin XML no hay recibo firmado para el proveedor: el recinto no viaja.
  const pideRecinto = !esReclamo && accion === 'ERM' && !sinXml
  // En un reclamo el motivo no es opcional: viaja al emisor y es lo único que
  // le dice qué corregir.
  const faltaMotivo = esReclamo && !observaciones.trim()

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={esReclamo ? 'Reclamar ante el SII' : 'Responder al SII'}
      size="sm"
      actions={
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-content-muted hover:text-content">
            Cancelar
          </button>
          <button
            type="button"
            disabled={isPending || faltaMotivo}
            onClick={() => onConfirm({
              accion,
              recinto: pideRecinto ? recinto.trim() : undefined,
              observaciones: observaciones.trim() || undefined,
            })}
            className={`px-3 py-1.5 text-sm rounded-lg text-on-primary disabled:opacity-50 ${
              esReclamo ? 'bg-danger-fill hover:bg-danger-fill-hover' : 'bg-primary hover:bg-primary-hover'
            }`}
          >
            {isPending ? 'Enviando…' : esReclamo ? 'Reclamar ante el SII' : 'Enviar al SII'}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-content-muted">
          {documento}
        </p>

        {sinXml && (
          <div className="flex gap-2 rounded-lg bg-warning-surface p-3 text-sm text-warning-content">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <span>
              Todavía no llega el XML de esta factura. La respuesta se registra en el SII con el RUT del
              proveedor, el tipo y el folio, pero no se le envía por correo. Sin el XML no se sabe si la
              factura es a crédito: si es al contado o ya pasaron 8 días desde que el SII la recibió, el SII
              la rechaza y se muestra su respuesta.
            </span>
          </div>
        )}

        {esReclamo && (
          <div className="flex gap-2 rounded-lg bg-danger-surface p-3 text-sm text-danger-content">
            <Ban size={16} className="shrink-0 mt-0.5" />
            <span>
              El reclamo anula la factura en Compras y revierte su asiento contable.
              No se puede deshacer.
            </span>
          </div>
        )}

        <fieldset className="space-y-2">
          <legend className="text-xs font-medium text-content-muted mb-1">
            {esReclamo ? '¿Qué pasó?' : '¿Qué informar al SII?'}
          </legend>
          {opciones.map(o => {
            const activa = accion === o.valor
            return (
              <label
                key={o.valor}
                className={`flex gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                  activa ? 'border-primary bg-primary-50 dark:bg-primary-950/30' : 'border-divider hover:bg-soft'
                }`}
              >
                <input
                  type="radio"
                  name="accion-sii"
                  className="mt-1"
                  checked={activa}
                  onChange={() => setAccion(o.valor)}
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-content">
                    {o.titulo}
                    <span className="ml-1.5 text-[11px] font-normal text-content-subtle">({o.valor})</span>
                  </span>
                  <span className="block text-xs text-content-muted mt-0.5">{o.detalle}</span>
                </span>
              </label>
            )
          })}
        </fieldset>

        {pideRecinto && (
          <div>
            <label htmlFor="acusesiimodal-recinto-de-recepcion" className="block text-xs font-medium text-content-muted mb-1">
              Recinto de recepción
            </label>
            <input id="acusesiimodal-recinto-de-recepcion"
              value={recinto}
              onChange={e => setRecinto(e.target.value)}
              maxLength={80}
              placeholder="Dónde se recibió la mercadería"
              className="w-full px-3 py-1.5 text-sm rounded-lg border border-divider bg-surface text-content focus:ring-2 focus:ring-focus-ring"
            />
            <p className="text-[11px] text-content-subtle mt-1">
              Se declara al SII y al proveedor. Corregirlo si la recepción fue en otra sucursal.
            </p>
          </div>
        )}

        <div>
          <label htmlFor="acusesii-observaciones" className="block text-xs font-medium text-content-muted mb-1">
            {esReclamo ? 'Motivo del reclamo' : 'Observaciones'}
            {esReclamo && <span className="text-danger-solid"> *</span>}
          </label>
          <textarea id="acusesii-observaciones"
            value={observaciones}
            onChange={e => setObservaciones(e.target.value)}
            rows={3}
            maxLength={240}
            placeholder={esReclamo
              ? 'Ej: la cantidad facturada no coincide con la guía de despacho 4471'
              : 'Opcional, queda en el historial del documento'}
            className="w-full px-3 py-1.5 text-sm rounded-lg border border-divider bg-surface text-content focus:ring-2 focus:ring-focus-ring"
          />
          <p className="text-[11px] text-content-subtle mt-1">
            {esReclamo
              ? 'Le llega al proveedor: es lo que le dice qué corregir.'
              : 'Queda en el historial del documento; no se envía al SII.'}
          </p>
        </div>

        {faltaMotivo && (
          <p className="flex items-center gap-1.5 text-xs text-warning-content-soft">
            <AlertTriangle size={13} /> Falta indicar el motivo del reclamo.
          </p>
        )}
      </div>
    </Modal>
  )
}
